import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SendIcon from '@mui/icons-material/Send';
import FormProvider from '../form/FormProvider';
import TextField from '../form/TextField';
import PhoneField from '../form/PhoneField';
import SelectField from '../form/SelectField';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';
import { Roles } from '../../constants';

/**
 * Shared invite user modal.
 */
const ROLE_LABELS = {
  [Roles.BrokerAdmin]: 'Broker Manager',
  [Roles.Broker]: 'Loan Officer',
  [Roles.Processor]: 'Processor',
};

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().email('Invalid email'),
  role: z.string().min(1, 'Role is required'),
  nmls: z.string().optional().default(''),
  phone: z.string().optional().default(''),
});

const ROLE_OPTIONS = [
  { value: Roles.Broker, label: 'Loan Officer' },
  { value: Roles.Processor, label: 'Processor' },
  { value: Roles.BrokerFullAccessReadOnly, label: 'Read-only (full pipeline & loans)' },
];


const InviteUserModal = ({ mode = 'lender', role: roleProp, onInvite, onClose }) => {
  const isBrokerMode = mode === 'broker';
  const defaultRole = roleProp || (isBrokerMode ? Roles.Broker : Roles.BrokerAdmin);
  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '', lastName: '', emailAddress: '',
      nmls: '', phone: '', role: defaultRole,
    },
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const watchedRole = methods.watch('role');

  const handleSubmit = async (data) => {
    setError('');
    setSubmitting(true);
    try {
      const payload = { ...data, phone: data.phone.replace(/\D/g, '') };
      await onInvite(payload);
      onClose();
    } catch (err) {
      setError(getAxiosErrorMessage(err, 'Failed to invite user'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isBrokerMode ? 'Invite User' : defaultRole === Roles.BrokerAdmin ? `Invite ${ROLE_LABELS[defaultRole]}` : 'Invite User'}</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {isBrokerMode && (
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
              New users will be in <strong>Pending Approval</strong> status until confirmed by a Lender Admin.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}><TextField name="firstName" label="First Name" required size="small" /></Grid>
            <Grid size={{ xs: 6 }}><TextField name="lastName" label="Last Name" required size="small" /></Grid>
          </Grid>
          <TextField name="emailAddress" label="Email" type="email" required size="small" sx={{ mb: 2 }} />
          {(isBrokerMode || (!isBrokerMode && defaultRole !== Roles.BrokerAdmin)) && (
            <SelectField name="role" label="Role" options={ROLE_OPTIONS} size="small" sx={{ mb: 2 }} />
          )}
          <TextField name="nmls" label="NMLS" size="small" required={watchedRole !== Roles.Processor && watchedRole !== Roles.BrokerFullAccessReadOnly} helperText={watchedRole === Roles.Processor || watchedRole === Roles.BrokerFullAccessReadOnly ? 'NMLS is optional for this role' : undefined} sx={{ mb: 2 }} />
          <PhoneField name="phone" label="Phone" size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}>
            {submitting ? 'Sending…' : isBrokerMode ? 'Request User' : 'Send Invite'}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default InviteUserModal;
